import { Button, Card, Divider, Input, Select, Tag, Table, Typography } from "antd";
import { JobInterface, JobTypeValues, JOB_STATUS } from "api/jobs/dto";
import { useAddressHelper } from "utils/useAddressHelper";
import { useGetJobsByRecruiter } from "api/jobs/useGetJobsByRecruiter";
import { useNavigate } from "react-router-dom";
import ErrorPage from "components/ui/ErrorPage";
import TableActionMenu from "./components/TableActionMenu";
import { QueryParamsStatus, useFilterPagination } from "hooks/useFilterPagination";
import { storage } from "utils/storage";
import { getApplicantCount } from "api/jobs/useGetJobDetails";
import React from "react";
import { InfoCircleOutlined } from "@ant-design/icons";


const { Option } = Select;
const { Search } = Input;
const { Title } = Typography;

const RecruiterJobList = () => {
  const user = storage.getUserFromStorage();
  const { queryParamaters, onSearch, onTableChange, onFilterChange } = useFilterPagination();

  const { data, isFetching, error } = useGetJobsByRecruiter({ ...queryParamaters, recruiterId: user.uid });

  const navigate = useNavigate();

  const {
    getProvinceOptions,
    getDistrictOptions,
    getMunicipalityOptions,
    getWardOptions,
  } = useAddressHelper();

  const ApplicantCount: React.FC<{ jobId: string }> = ({ jobId }) => {
    const [applicantCount, setApplicantCount] = React.useState<number | null>(null);

    React.useEffect(() => {
      const fetchApplicantCount = async () => {
        const count = await getApplicantCount(jobId);
        setApplicantCount(count);
      };

      fetchApplicantCount();
    }, [jobId]);

    return <p>{!applicantCount ? "No applications received." : applicantCount}</p>;
  };


  const getStatusColor = (status: JOB_STATUS | undefined): string => {
    switch (status) {
      case JOB_STATUS.DRAFT:
        return 'cyan';
      case JOB_STATUS.PUBLISHED:
        return 'green';
      case JOB_STATUS.FILLED:
        return 'geekblue';
      case JOB_STATUS.EXPIRED:
        return 'red';
      default:
        return 'default'; 
    }
  };

  const columns = [
    {
      title: "Job Title",
      dataIndex: "jobTitle",
      key: "jobTitle",
    },
    {
      title: "Status",
      dataIndex: "status", // Assuming `status` is in your data
      key: "status",
      render: (__: any, record: JobInterface) => (
        <Tag
          icon={<InfoCircleOutlined />}
          color={getStatusColor(record.status)}
        >
          {record.status}
        </Tag>
      ),
    },
    {
      title: "Applied",
      key: "totalNumberOfPeopleApplied",
      render: (__: any, record: JobInterface) => <ApplicantCount jobId={record.id} />,
    },
    {
      title: "Vacancies",
      key: "vacancies",
      render: (__: any, record: JobInterface) => (
        <div className="px-5 py-2 border-b">
          <p>
            <strong>No of Vacancy:</strong>{" "}
            {Array.isArray(record.numberOfVacancy)
              ? record.numberOfVacancy.join(", ")
              : record.numberOfVacancy}
          </p>
          <p>
            <strong>Required Skills:</strong>{" "}
            {record.skills?.length > 0 ? record.skills.join(", ") : "Not Specified"}
          </p>
          {record.joblocation?.map((location, index) => (
            <div key={index} className="mt-2">
              <p>
                <strong>Location {index + 1}:</strong> {location.district}, {location.province}
              </p>
              <p>
                {location.languages && location.languages.length > 0 ? (
                  <div className="text-gray-700 mb-2">
                    Languages: {location.languages.join(", ")}
                  </div>
                ) : (
                  ""
                )}
              </p>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (__: any, record: JobInterface) => <TableActionMenu id={record?.id} />,
    },
  ];


  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <div className="bg-background">
      <div className="max-w-6xl mx-auto py-12">
        <Card>
          <Title level={2} style={{ fontWeight: 700 }}>
            Job List
          </Title>
          <div className="flex flex-col md:flex-row gap-y-5 justify-between mt-5">
            <Search
              placeholder="Search for job"
              onSearch={(value) => onSearch(value, QueryParamsStatus.SEARCH_BY)}
              style={{ width: 280 }}
              allowClear
              enterButton="Search"
              size="large"
              className="__search__input"
            />
            <Button
              type="primary"
              className="h-10 px-6 border-2 border-border text-base flex items-center justify-center gap-2 bg-primary text-white border-none"
              onClick={() => navigate("/recruiter/post-job")}
            >
              Post Job
            </Button>
          </div>
          <Divider />
          <div className="flex flex-col md:flex-row gap-y-5 justify-between">
            <div className="flex flex-col gap-y-5 md:flex-row md:gap-x-2">
              <Select
                allowClear
                style={{ border: "none", width: "90%" }}
                placeholder="Select Province"
                className="__job__type"
                options={getProvinceOptions()}
                onChange={(value) => {
                  onFilterChange(JSON.parse(value)?.name, QueryParamsStatus.FIND_BY_PROVINCE);
                }}
              />
              <Select
                allowClear
                style={{ border: "none", width: "90%" }}
                placeholder="Select District"
                className="__job__type"
                optionFilterProp="children"
                filterOption={(input, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                onChange={(value) => onFilterChange(value, QueryParamsStatus.FIND_BY_DISTRICT)}
              >
                {getDistrictOptions()?.map((district) => (
                  <Option key={district?.label} value={district?.label}>
                    {district?.label}
                  </Option>
                ))}
              </Select>
              <Select
                allowClear
                style={{ border: "none", width: "90%" }}
                placeholder="Select Municipality"
                className="__job__type"
                filterOption={(input, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                onChange={(value) => onFilterChange(value, QueryParamsStatus.FIND_BY_MUNICIPALITY)}
              >
                {getMunicipalityOptions()?.map((municipality) => (
                  <Option key={municipality?.label} value={municipality?.label}>
                    {municipality?.label}
                  </Option>
                ))}
              </Select>
              <Select
                allowClear
                style={{ border: "none", width: "90%" }}
                placeholder="Select Ward"
                className="__job__type"
                options={getWardOptions()}
                onChange={(value) => onFilterChange(value, QueryParamsStatus.FIND_BY_WARD)}
              />
              <Select
                value={queryParamaters?.findByJobType}
                allowClear
                style={{ border: "none", width: "90%" }}
                placeholder="Sort by Job Type"
                className="__job__type"
                options={[
                  { value: JobTypeValues.FULL_TIME, label: "Full Time" },
                  { value: JobTypeValues.PART_TIME, label: "Part Time" },
                  { value: JobTypeValues.CONTRACT, label: "Contract" },
                  { value: JobTypeValues.INTERNSHIP, label: "Internship" },
                ]}
                onChange={(value) => onFilterChange(value, QueryParamsStatus.FIND_BY_JOB_TYPE)}
              />
            </div>
          </div>
          <Table
            dataSource={data?.data}
            columns={columns}
            className="mt-12"
            loading={isFetching}
            pagination={{
              total: data?.totalData,
              current: data?.page,
              defaultPageSize: 10,
              defaultCurrent: 1,
              onChange: onTableChange,
              position: ["bottomLeft"],
            }}
            scroll={{ x: true }}
          />
        </Card>
      </div>
    </div>
  );
};

export default RecruiterJobList;
